import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { RootState } from "../globals";

import { activarAvisos, avisoLocal, instalada } from "../appMovil/appMovil";
import { push } from "../navigation/v2/sales/push";
import { Aviso, abrirEnlace, avisosApi } from "./avisos.service";
import { iniciarConteo, juntarRafaga, refrescarConteo, useConteoAvisos } from "./conteoAvisos";
import "./avisos.css";

/**
 * Los avisos "en grande" con WAPP abierta (Esteban, 2026-10-10: "en la PC lo tienen que VER, como WhatsApp Web"):
 * - Tarjeta arriba a la derecha (máximo 3 a la vez): la urgente se queda hasta cerrarla; la normal se va sola a los 8 s
 *   de estar a la vista. Título, texto y "Ver".
 * - Notificación del sistema (avisoLocal) si la pestaña está atrás o la ventana sin foco. Usa el mismo tag que el push del
 *   servidor (aviso-<id>), así que si llegan los dos queda uno.
 * - Solo lo que la persona quiere al momento por el celular o por WappTeam y fuera de su silencio nocturno: el servidor lo
 *   marca como "notificar" en cada aviso.
 * - Si el navegador no tiene permiso de notificaciones, una sola vez, la franja para activarlas.
 * Se entera al instante por wapp-push (evento "aviso") y, de respaldo, cuando sube el número de sin leer (cada minuto y al
 * volver a la pestaña). Ese conteo lo arrancaba la campanita de la barra; se quitó el 2026-10-11 (repetía lo de IA Washington
 * en WappTeam y en el celular tapaba el logo), así que ahora lo arranca este componente.
 */
const MAX_TARJETAS = 3;
const SE_VA_MS = 8000;
/*
	El permiso de notificaciones es por dominio: en wapp.washingtonacademy.com arranca sin dar, pero el puente copió la marca
	de app.washingtonacademy.mx y la franja ya no salía (2026-10-21). La marca lleva el dominio para que se pida una vez aquí.
*/
const FRANJA_KEY = "wapp.franjaAvisos." + window.location.hostname;

interface Tarjeta extends Aviso { visto: number | null; }

const recortar = (t: string | null, n: number) => {
	const s = (t || "").replace(/\s+/g, " ").trim();
	return s.length > n ? s.slice(0, n - 1) + "…" : s;
};

const franjaPendiente = (): boolean => {
	if (typeof Notification === "undefined" || Notification.permission === "granted" || instalada()) return false;
	try { return !localStorage.getItem(FRANJA_KEY); } catch { return false; }
};

const AvisosEnVivo: React.FC = () => {
	const campusId: number | null = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampusId) || null;
	const navigate = useNavigate();
	const conteo = useConteoAvisos();
	const [tarjetas, setTarjetas] = useState<Tarjeta[]>([]);
	const [franja, setFranja] = useState<boolean>(false);
	const [bloqueadas, setBloqueadas] = useState<boolean>(false);
	const ultimo = useRef<number | null>(null);
	const pidiendo = useRef(false);
	const otraVez = useRef(false);
	const conteoAntes = useRef(0);

	// El último aviso que ya existía al entrar: de ahí en adelante, lo nuevo se enseña.
	useEffect(() => {
		ultimo.current = null;
		avisosApi.bandeja({ limite: 1 }).then((r) => { ultimo.current = r.data.avisos[0]?.id ?? 0; }).catch(() => { ultimo.current = 0; });
		const t = setTimeout(() => {
			if (franjaPendiente()) { setFranja(true); setBloqueadas(Notification.permission === "denied"); }
		}, 2500);
		return () => clearTimeout(t);
	}, [campusId]);

	const nuevos = useCallback(async () => {
		if (ultimo.current === null) return;
		// Si ya hay una consulta en camino, se repite al terminar (lo que llegó mientras tanto no se pierde).
		if (pidiendo.current) { otraVez.current = true; return; }
		pidiendo.current = true;
		try {
			const r = await avisosApi.bandeja({ desde: ultimo.current, limite: 10 });
			const lista = [...r.data.avisos].sort((a, b) => a.id - b.id);
			if (lista.length) ultimo.current = lista[lista.length - 1].id;
			const mostrar = lista.filter((a) => a.notificar && !a.leido);
			if (!mostrar.length) return;
			const atras = document.visibilityState !== "visible" || !document.hasFocus();
			mostrar.forEach((a) => {
				if (atras) avisoLocal(a.titulo, recortar(a.cuerpo, 180), "aviso-" + a.id, a.enlace || "/avisos");
			});
			setTarjetas((t) => {
				const vistas = mostrar.map((a) => ({ ...a, visto: document.visibilityState === "visible" ? Date.now() : null }));
				return [...vistas.reverse(), ...t.filter((x) => !mostrar.some((m) => m.id === x.id))];
			});
		} catch { /* sin red: llegan con la siguiente */ } finally {
			pidiendo.current = false;
			if (otraVez.current) { otraVez.current = false; setTimeout(() => nuevosRef.current(), 300); }
		}
	}, []);

	// Una ráfaga de avisos (wapp-push) o de subidas del conteo se junta en UNA consulta de la bandeja (ver juntarRafaga).
	const rafaga = useRef(juntarRafaga(() => { nuevosRef.current(); }));
	const nuevosRef = useRef(nuevos);
	nuevosRef.current = nuevos;
	useEffect(() => () => rafaga.current.cancelar(), []);

	// Al instante (wapp-push) y, de respaldo, cuando sube el número de sin leer.
	useEffect(() => {
		if (!campusId) return;
		const soltar = push.usar(campusId);
		const pararConteo = iniciarConteo(campusId, false);
		const quitar = push.on("aviso", rafaga.current.pedir);
		return () => { quitar(); pararConteo(); soltar(); };
	}, [campusId]);
	useEffect(() => {
		if (conteo > conteoAntes.current) rafaga.current.pedir();
		conteoAntes.current = conteo;
	}, [conteo]);

	// Las normales se van solas 8 s después de que la persona las tuvo a la vista.
	useEffect(() => {
		const marcar = () => {
			if (document.visibilityState !== "visible") return;
			setTarjetas((t) => t.map((x) => (x.visto ? x : { ...x, visto: Date.now() })));
		};
		document.addEventListener("visibilitychange", marcar);
		const ciclo = setInterval(() => {
			const ahora = Date.now();
			setTarjetas((t) => {
				const quedan = t.filter((x) => x.prioridad === "urgente" || !x.visto || ahora - x.visto < SE_VA_MS);
				return quedan.length === t.length ? t : quedan;
			});
		}, 1000);
		return () => { document.removeEventListener("visibilitychange", marcar); clearInterval(ciclo); };
	}, []);

	const cerrar = (id: number) => setTarjetas((t) => t.filter((x) => x.id !== id));
	const ver = async (a: Tarjeta) => {
		cerrar(a.id);
		try { await avisosApi.leer({ ids: [a.id] }); refrescarConteo(0); } catch { /* nada */ }
		if (a.enlace) abrirEnlace(a.enlace, navigate); else navigate("/avisos");
	};
	const cerrarFranja = () => {
		try { localStorage.setItem(FRANJA_KEY, String(Date.now())); } catch { /* nada */ }
		setFranja(false);
	};
	const activar = async () => {
		try {
			const r = await activarAvisos();
			if (r === "bloqueados") { setBloqueadas(true); return; }
		} catch {
			try { await Notification.requestPermission(); } catch { /* nada */ }
		}
		cerrarFranja();
	};

	const visibles = tarjetas.slice(0, MAX_TARJETAS);
	return (
		<div className="avt-pila" aria-live="polite">
			{franja ? (
				<div className="avf" role="region" aria-label="Notificaciones de WAPP">
					<span className="avf-icono" aria-hidden="true">
						<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
							<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
						</svg>
					</span>
					<span className="avf-txt">
						<b>Activa Las Notificaciones De WAPP</b>
						<span>{bloqueadas ? "El navegador las tiene bloqueadas: dales permiso en el candado junto a la dirección." : "Ves cada aviso aunque estés en otra pestaña."}</span>
					</span>
					<span className="avf-botones">
						<button className="av-btn" onClick={cerrarFranja}>{bloqueadas ? "Entendido" : "Ahora No"}</button>
						{!bloqueadas ? <button className="av-btn pri" onClick={activar}>Activar</button> : null}
					</span>
				</div>
			) : null}
			{visibles.map((a) => (
						<div key={a.id} className={"avt" + (a.prioridad === "urgente" ? " urgente" : "")} role={a.prioridad === "urgente" ? "alert" : "status"}>
							<div className="avt-cab">
								<span className="avt-grupo">{a.prioridad === "urgente" ? <em>Urgente</em> : null}{a.grupo}{a.plantel ? " · " + a.plantel : ""}</span>
								<button className="avt-x" aria-label="Cerrar aviso" onClick={() => cerrar(a.id)}>×</button>
							</div>
							<b className="avt-tit">{a.titulo}</b>
							{a.cuerpo ? <p className="avt-cuerpo">{recortar(a.cuerpo, 220)}</p> : null}
							<div className="avt-pie">
								{tarjetas.length > MAX_TARJETAS && a === visibles[visibles.length - 1] ? (
									<button className="avt-mas" onClick={() => { setTarjetas([]); navigate("/avisos"); }}>+{tarjetas.length - MAX_TARJETAS} más</button>
								) : <span />}
								<button className="av-btn pri chico" onClick={() => ver(a)}>Ver</button>
							</div>
						</div>
					))}
		</div>
	);
};

export default AvisosEnVivo;
